import { Callout } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { participantAttributeKey } from '../participantAttributes';

import type { IndexedParticipantAttribute } from '../participantAttributes';
import type { TranslationKey } from '@/i18n-key';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';

import {
    ObjectiveParticipantAttributeType,
    type ObjectiveParticipantAttributeTypeValue,
} from '@/constants/ObjectiveParticipantAttributeType';

const NO_EMPLOYEE_KEYS: Record<ObjectiveParticipantAttributeTypeValue, TranslationKey> = {
    [ObjectiveParticipantAttributeType.DEPARTMENT]:
        'objectives.form.participantPicker.noEmployeeIn.department',
    [ObjectiveParticipantAttributeType.JOB_TITLE]:
        'objectives.form.participantPicker.noEmployeeIn.jobTitle',
    [ObjectiveParticipantAttributeType.COMPANY_ADDRESS]:
        'objectives.form.participantPicker.noEmployeeIn.location',
    [ObjectiveParticipantAttributeType.EMPLOYEE_LEVEL]:
        'objectives.form.participantPicker.noEmployeeIn.employeeLevel',
    [ObjectiveParticipantAttributeType.MANAGER]:
        'objectives.form.participantPicker.noEmployeeIn.team',
    [ObjectiveParticipantAttributeType.CUSTOM_ATTRIBUTE]:
        'objectives.form.participantPicker.noEmployeeIn.customValue',
};

interface ObjectiveEmptyAttributesNoticeProps {
    /** The values picked that bring in no one within the reader's reach, as the server counted them. */
    attributes: ObjectiveParticipantAttribute[];
    attributesByKey: ReadonlyMap<string, IndexedParticipantAttribute> | undefined;
}

/** Says which picked values bring in none of the reader's employees: their chip alone reads as a
 * choice that took effect. */
export default function ObjectiveEmptyAttributesNotice({
    attributes,
    attributesByKey,
}: ObjectiveEmptyAttributesNoticeProps) {
    const { t } = useTranslation();

    const named = attributes.flatMap((attribute) => {
        const name = attributesByKey?.get(participantAttributeKey(attribute))?.option.name;
        return name
            ? [{ key: participantAttributeKey(attribute), type: attribute.type, name }]
            : [];
    });

    if (named.length === 0) {
        return null;
    }

    return (
        <Callout
            tone={'warning'}
            role={'status'}
            dataTestId={'objective-empty-attributes-notice'}
        >
            {named.map(({ key, type, name }) => (
                <p key={key}>{t(NO_EMPLOYEE_KEYS[type], { name })}</p>
            ))}
        </Callout>
    );
}
